import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Integration, IntegrationType } from '@pdqdev/pdq-shared/dist/entities';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ApiService } from '@core/services/api/api.service';
import { IntegrationsService } from '@services/integrations/integrations.service';
import { ToastService } from '@storybook/services/toast/toast.service';
import { TimeUnits } from '@utils/times-utils';

interface MetricToggle {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
}

const METRIC_DEFINITIONS: Omit<MetricToggle, 'enabled'>[] = [
  { id: 'shipping_costs', label: 'Shipping Cost per Order', description: "Seller's shipping expense per order" },
  { id: 'blended_roas', label: 'ROAS (Blended)', description: 'Revenue ÷ Total Ad Spend' },
  { id: 'blended_cpa', label: 'CAC (Blended CPA)', description: 'Ad Spend ÷ Orders' },
  { id: 'ncpa', label: 'New Customer CAC (NCPA)', description: 'Ad Spend ÷ New Customer Orders' },
  { id: 'net_profit', label: 'Net Profit', description: 'Revenue − All Costs' },
  { id: 'poas', label: 'POAS', description: 'Profit on Ad Spend' },
  { id: 'net_margin', label: 'Net Margin', description: 'Net Profit ÷ Revenue' },
  { id: 'mer', label: 'MER', description: 'Marketing Efficiency Ratio' },
  { id: 'nc_roas', label: 'NC ROAS', description: 'New Customer ROAS' },
  { id: 'blended_ad_spend', label: 'Total Ad Spend', description: 'All channel ad spend' },
  { id: 'cost_of_goods', label: 'COGS', description: 'Cost of goods sold' },
  { id: 'handling_fees', label: 'Handling Fees', description: 'Fulfillment/packaging costs' },
  { id: 'payment_gateway_costs', label: 'Payment Gateway Costs', description: 'Transaction fees' },
];

@Component({
  selector: 'triple-whale-metrics',
  templateUrl: './triple-whale-metrics.component.html',
  styleUrls: ['./triple-whale-metrics.component.scss'],
  standalone: false,
})
export class TripleWhaleMetricsComponent implements OnInit, OnDestroy {
  @Input() integrationType: IntegrationType;
  @Input() integration: Integration | any;

  metrics: MetricToggle[] = [];

  private destroy$ = new Subject<void>();
  private metricsChanged$ = new Subject<void>();
  private saving = false;

  constructor(
    private apiService: ApiService,
    private integrationService: IntegrationsService,
    private toastService: ToastService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.integrationService.integrations$.pipe(takeUntil(this.destroy$)).subscribe((integrations) => {
      this.integration = integrations[this.integrationType.name]?.instance;
      if (!this.saving) {
        this.initMetrics();
      }
    });

    this.metricsChanged$
      .pipe(takeUntil(this.destroy$), debounceTime(TimeUnits._SECOND_IN_MS))
      .subscribe(() => this.saveMetrics());
  }

  initMetrics() {
    const enabledMetrics: string[] = this.integration?.metadata?.enabled_metrics ?? [];
    this.metrics = METRIC_DEFINITIONS.map((def) => ({
      ...def,
      enabled: enabledMetrics.includes(def.id),
    }));
  }

  handleToggle(metricId: string, enabled: boolean) {
    const metric = this.metrics.find((m) => m.id === metricId);
    if (metric) {
      metric.enabled = enabled;
      this.metricsChanged$.next();
    }
  }

  private saveMetrics() {
    this.saving = true;
    const enabledMetrics = this.metrics.filter((m) => m.enabled).map((m) => m.id);
    this.apiService
      .post(`integrations/metadata/${this.integration?.id}`, {
        key: 'enabled_metrics',
        value: enabledMetrics,
      })
      .subscribe({
        next: (updatedIntegration) => {
          this.integrationService.updateIntegrationState(updatedIntegration);
          this.saving = false;
        },
        error: () => {
          this.toastService.showToast({ mode: 'danger', message: 'Failed to save metrics settings' });
          this.saving = false;
        },
      });
  }
}
